import { Add } from '@material-ui/icons'
import { Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItemButton, ListItemText, Stack, SwipeableDrawer, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getIsMobileApp } from '../../utils/mobileApp'
import { Lineup, LineupBuilderState } from './store/types'
import { createLineup, deleteAllLineups, deleteLineup, setCsvHeaders, setLineupRules, setSelectedLineup } from './store/actions'
import SingleLineup from './SingleLineup'
import { getSiteForShowdown } from '../utils/showdownHandler'
import { CSVLink } from 'react-csv'
import { CopyAll, Delete, FavoriteBorderOutlined, Favorite, Info, InfoOutlined } from '@mui/icons-material'
import { getFilename } from '../common/helpers/FilenameHelper'
import LineupNameEditor from './LineupNameEditor'
import EntryManager from '../preContest/EntryManager'
import { useLocalStorage } from 'usehooks-ts'
import { LineupApiResponse } from '../preContest/queries/queryTypes'
import { convertLineupToApiResponse, isLineupComplete } from './favoriteSnapshot'
import { SlateResponse } from '../common/store/types'
import { ABOVE_BOTTOM_NAV_ZINDEX } from '../common/MobileAppBottomNav'

const SORT_OPTIONS = [
  { label: 'ROI', value: 'roi' },
  { label: 'Win %', value: 'win' },
  { label: 'Projection', value: 'proj' },
];

const LineupsHeader: React.FC<{
  sort: string;
  setSortBy: (s: string) => void;
  canExport: boolean;
  getCsvData: () => string;
  selectedSlate: SlateResponse;
}> = ({ sort, setSortBy, canExport, getCsvData, selectedSlate }) => {
  const isMobileApp = getIsMobileApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);

  const sortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label || 'ROI';

  // Desktop: original layout
  if (!isMobile) {
    return (
      <Stack padding='16px' direction='row' alignItems='center' justifyContent='space-between' columnGap='15px' flexWrap='nowrap' rowGap='10px'>
        <Stack direction='row' alignItems='center' columnGap='8px' sx={{ whiteSpace: 'nowrap', flexGrow: 1 }}>
          <Stack fontSize='24px' fontWeight={700}>My Lineups</Stack>
          <Tooltip title={<Stack><ul>
            <li>ROI: Estimated return on investment for your lineup</li>
            <li>WIN: The likelihood of your lineup finishing in first place</li>
            <li>PROJ: Total projected score for that lineup</li>
          </ul></Stack>}>
            <Stack direction='column' alignItems='center' fontWeight={700}>
              <Stack fontSize='14px'>Sort By</Stack>
              <Stack direction='row' columnGap='4px' alignItems='center' fontSize='12px'>
                {SORT_OPTIONS.map((opt, i) => (
                  <React.Fragment key={opt.value}>
                    {i > 0 && <Stack>/</Stack>}
                    <Stack sx={{ cursor: 'pointer', color: sort === opt.value ? 'white' : 'rgba(255, 255, 255, 0.5)' }} onClick={() => setSortBy(opt.value)}>
                      {opt.label.toUpperCase()}
                    </Stack>
                  </React.Fragment>
                ))}
              </Stack>
            </Stack>
          </Tooltip>
        </Stack>
        <Stack direction='row' columnGap='10px' alignItems='center'>
          {canExport ? (
            <Button size='small' variant='outlined' sx={{ padding: '8px 10px', borderRadius: '6px' }}>
              <CSVLink data={getCsvData()} filename={getFilename(selectedSlate, `_Single_Lineup_Sim_Lineups`)} target="_blank"
                style={{ color: 'white', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, textDecoration: 'none', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                Export Lineups
              </CSVLink>
            </Button>
          ) : (
            <Button size='small' variant='contained' disabled sx={{ '&.Mui-disabled': { backgroundColor: '#333', color: 'rgba(255,255,255,0.5)' } }}>Export Lineups</Button>
          )}
          <Stack direction='row' alignItems='center' columnGap='5px'>
            <Tooltip title="Assign lineups to contests and create your upload file.">
              <InfoOutlined />
            </Tooltip>
            <EntryManager showIcon={false} bgColor='palette.primary' localStorageKey={`lineupbuilder-favorites-${selectedSlate.slateId}`} />
          </Stack>
        </Stack>
      </Stack>
    );
  }

  // Mobile layout
  return (
    <Stack padding='12px' rowGap='10px'>
      {/* Row 1: Title (hidden in mobile app) */}
      {!isMobileApp && <Stack fontSize='22px' fontWeight={700}>My Lineups</Stack>}

      {/* Row 2: Sort | Export + Entry Manager */}
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <ButtonBase
          onClick={() => setSortDrawerOpen(true)}
          sx={{
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '20px',
            padding: '6px 12px',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.87)',
            whiteSpace: 'nowrap',
          }}
        >
          Sort: {sortLabel}
        </ButtonBase>
        <Stack direction='row' alignItems='center' gap={1}>
          {canExport ? (
            <Button size='small' variant='outlined' sx={{ padding: '8px 10px', borderRadius: '6px' }}>
              <CSVLink data={getCsvData()} filename={getFilename(selectedSlate, `_Single_Lineup_Sim_Lineups`)} target="_blank"
                style={{ color: 'white', fontFamily: 'Inter', fontSize: '12px', lineHeight: 'normal', fontWeight: 500, textDecoration: 'none', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                Export
              </CSVLink>
            </Button>
          ) : (
            <Button size='small' variant='contained' disabled sx={{ '&.Mui-disabled': { backgroundColor: '#333', color: 'rgba(255,255,255,0.5)' } }}>Export</Button>
          )}
          <EntryManager showIcon={false} bgColor='palette.primary' localStorageKey={`lineupbuilder-favorites-${selectedSlate.slateId}`} />
        </Stack>
      </Stack>

      {/* Sort bottom sheet */}
      <SwipeableDrawer
        anchor="bottom"
        open={sortDrawerOpen}
        onOpen={() => {}}
        onClose={() => setSortDrawerOpen(false)}
        disableSwipeToOpen
        sx={{ zIndex: ABOVE_BOTTOM_NAV_ZINDEX }}
        PaperProps={{
          sx: { backgroundColor: '#1A1A2E', borderTopLeftRadius: 16, borderTopRightRadius: 16, pt: 1, pb: 2 },
        }}
      >
        <Stack sx={{ width: '40px', height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '8px', margin: '8px auto 16px' }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1, px: 2 }}>Sort By</Typography>
        <List disablePadding>
          {SORT_OPTIONS.map(({ label, value }) => (
            <ListItemButton key={value} selected={sort === value}
              onClick={() => { setSortBy(value); setSortDrawerOpen(false); }}
              sx={{ px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
              <ListItemText primary={label} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
            </ListItemButton>
          ))}
        </List>
      </SwipeableDrawer>
    </Stack>
  );
};

const Lineups = () => {
  const { lineups, selectedLineupId, csvHeaders, lineupRubric, defaultRandomness } = useSelector<any, LineupBuilderState>((state: any) => state.lineupBuilder);
  const { selectedSlate } = useSelector((state: any) => state.common);
  const [canExport, setCanExport] = React.useState(false);
  const [sort, setSortBy] = React.useState('roi');
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const dispatch = useDispatch();

  const getCsvData = (): string => {
    let csvData = csvHeaders;
    const fullLineups = lineups.filter(x => x.players.filter(x => x.name !== '').length === lineupRubric.length);

    if (selectedSlate?.sport && selectedSlate?.type) {
      fullLineups.forEach((x) => {
        x.players.forEach((player) => {
          if (player.nameAndId !== '') {
            csvData = csvData.concat(player.nameAndId + ",");
          }
        })
        csvData = csvData.concat("\n");
      });
    }

    return csvData
  };

  const onCreateNewLineup = () => {
    const nextId = lineups.reduce((max: number, x: Lineup) => Math.max(max, x.id), 0) + 1;
    dispatch(createLineup({
      id: nextId,
      fpp: 0,
      ownership: 0,
      players: lineupRubric,
      simResults: [],
      simStatus: 'incomplete',
      lineupChecked: false,
      lineupErrors: [],
      randomness: defaultRandomness, // Use global default randomness
    } as Lineup));
    dispatch(setSelectedLineup(nextId))
  }

  const onDeleteAllLineups = () => {
    // Clear Redux store - localStorage will be updated by the useEffect in index.tsx
    dispatch(deleteAllLineups());
  }

  React.useEffect(() => {
    const saveCsvHeaders = async () => {
      let csvData = "";
      const header = await fetch(
        `${process.env.REACT_APP_API_URL}/api/contests/csvHeaders?sport=${selectedSlate?.sport
        }&site=${selectedSlate?.type?.toUpperCase() === "SHOWDOWN"
          ? getSiteForShowdown(selectedSlate?.site)
          : selectedSlate?.site
        }`
      ).then((res) => res.json());
      csvData = header + "\n";

      dispatch(setCsvHeaders(csvData))
      return csvData;
    };
    if (selectedSlate?.sport && selectedSlate?.site) {
      saveCsvHeaders();
    }

    if (selectedSlate?.lineupRules && selectedSlate?.lineupRules?.positions) {
      dispatch(setLineupRules(selectedSlate.lineupRules.positions))
    }

  }, [selectedSlate]);

  React.useEffect(() => {
    if (lineups.length > 0) {
      const fullLineups = lineups.filter(x => x.players.filter(x => x.name !== '').length === lineupRubric.length);
      if (fullLineups.length > 0) {
        setCanExport(true)
      } else {
        setCanExport(false)
      }
    }
  }, [lineups]);

  return (
    <Stack height={{ lg: '100%' }} bgcolor='#1C1C1C' borderRadius={{ xs: '0', lg: '10px' }} fontFamily='Inter' overflow='hidden' display='flex' flexDirection='column' flex={{ xs: 1 }} minHeight={0}>
      {
        !selectedLineupId && (
          <>
            <LineupsHeader
              sort={sort}
              setSortBy={setSortBy}
              canExport={canExport}
              getCsvData={getCsvData}
              selectedSlate={selectedSlate}
            />
            <Stack sx={{
              overflowY: 'auto',
              height: 'calc(100% - 150px)',
            }}>
              {
                lineups && [...lineups]
                  .sort((a, b) => {
                    if (sort === 'roi') {
                      return parseFloat(b.simResults?.simRoi || '0') - parseFloat(a.simResults?.simRoi || '0')
                    } else if (sort === 'win') {
                      return parseFloat(b.simResults?.winPct || '0') - parseFloat(a.simResults?.winPct || '0')
                    } else if (sort === 'proj') {
                      return b?.players.reduce((acc, player) => acc + player.fpp, 0) - a?.players.reduce((acc, player) => acc + player.fpp, 0)
                    } else {
                      return parseFloat(b.simResults?.simRoi || '0') - parseFloat(a.simResults?.simRoi || '0')
                    }
                  })
                  .map((lineup: Lineup) => (
                    <Stack key={lineup.id}>
                      <LineupSummary key={lineup.id} lineup={lineup} />
                    </Stack>
                  ))
              }
            </Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='center' padding='16px 10px' bgcolor='#1C1C1C' zIndex={1} borderTop='1px solid rgba(255, 255, 255, 0.15)' position='sticky' bottom={0} mt='auto' pb={getIsMobileApp() ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))'}>
              {lineups.length > 0 ? (
                <>
                  <Button variant='text' color='error' size='small' onClick={() => setDeleteDialogOpen(true)} sx={{ fontSize: '12px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                    Delete All
                  </Button>
                  <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
                    PaperProps={{ sx: { backgroundColor: '#1C1C1C', color: 'white', borderRadius: '12px' } }}>
                    <DialogTitle sx={{ fontWeight: 600 }}>Delete All Lineups</DialogTitle>
                    <DialogContent>
                      <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Are you sure you want to delete all of your lineups? This action cannot be undone.
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'white', textTransform: 'none' }}>Cancel</Button>
                      <Button onClick={() => { onDeleteAllLineups(); setDeleteDialogOpen(false); }} color='error' variant='contained' sx={{ textTransform: 'none' }}>Delete All</Button>
                    </DialogActions>
                  </Dialog>
                </>
              ) : <Stack />}
              <Button variant={lineups.length > 0 ? 'outlined' : 'contained'} onClick={() => onCreateNewLineup()} sx={{ backgroundColor: lineups.length > 0 ? 'rgba(67, 149, 255, 0.10)' : '', fontSize: '14px', fontWeight: 500 }}><Add /> Create New Lineup</Button>
            </Stack>
          </>
        )
      }
      {
        selectedLineupId && (
          <SingleLineup lineup={lineups.find(x => x.id === selectedLineupId) || {} as Lineup} />
        )
      }
    </Stack>
  )
}

const LineupSummary = ({ lineup }: { lineup: Lineup }) => {
  const { lineups, lineupRubric } = useSelector((state: any) => state.lineupBuilder);
  const { selectedSlate } = useSelector((state: any) => state.common);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [savedLineups, setSavedLineups] = useLocalStorage<LineupApiResponse[]>(
    `lineupbuilder-favorites-${selectedSlate.slateId}`,
    []
  );
  const canFavorite = isLineupComplete(lineup)

  const setFavoriteLineup = (lineup: LineupApiResponse) => {
    const tierLocked = false;
    if (!tierLocked) {
      let alreadySavedLineups = savedLineups;
      if (alreadySavedLineups.some((x) => x.id === lineup.id)) {
        alreadySavedLineups = alreadySavedLineups.filter(
          (x) => x.id !== lineup.id
        );
        setSavedLineups(alreadySavedLineups);
      } else {
        alreadySavedLineups.push(lineup);
        const sortedLineups = alreadySavedLineups?.sort((a, b) => b.simLineupRoi - a.simLineupRoi);
        setSavedLineups(sortedLineups);
      }
    }
  };

  const onSelectLineup = () => {
    dispatch(setSelectedLineup(lineup.id))
  }

  const onDupeLineup = () => {
    const nextId = lineups.reduce((max: number, x: Lineup) => Math.max(max, x.id), 0) + 1;
    dispatch(createLineup({
      ...lineup,
      id: nextId,
      randomness: lineup.randomness !== undefined ? lineup.randomness : 0, // Preserve randomness when duplicating
    } as Lineup));
  }

  const onDeleteLineup = () => {
    // Remove from precontest-lineups localStorage if it exists
    let alreadySavedLineups = savedLineups;
    if (alreadySavedLineups.some((x) => x.id === lineup.id)) {
      alreadySavedLineups = alreadySavedLineups.filter(
        (x) => x.id !== lineup.id
      );
      setSavedLineups(alreadySavedLineups);
    }
    // Dispatch delete from Redux store - this will trigger localStorage update in index.tsx
    dispatch(deleteLineup(lineup.id));
  }

  return (
    <Stack padding={isMobile ? '10px 12px' : '12px 16px'} rowGap={isMobile ? 1 : 1.5} sx={{
      cursor: 'pointer',
      borderTop: '1px solid rgba(255, 255, 255, 0.12)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
    }}
      onClick={onSelectLineup}
    >
      {/* Row 1: Name + player count + favorite */}
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Stack direction='row' columnGap='8px' alignItems='center' overflow='hidden' flex={1}>
          <Stack fontSize={isMobile ? '16px' : '18px'} fontWeight={700} overflow='hidden'>
            <LineupNameEditor sportsbook={selectedSlate?.site} slateId={selectedSlate?.slateId} lineupId={lineup.id} canEdit={false} />
          </Stack>
        </Stack>
        <Stack direction='row' alignItems='center' gap={1}>
          <Stack whiteSpace='nowrap' fontSize={isMobile ? '11px' : '14px'} color='rgba(255,255,255,0.5)'>
            {lineup.players.filter(x => x.name !== '').length}/{lineupRubric.length}
          </Stack>
          <IconButton aria-label="Favorite lineup" disabled={!canFavorite} sx={{ width: 40, height: 40 }} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (canFavorite) setFavoriteLineup(convertLineupToApiResponse(lineup));
          }}>
            {selectedSlate.slateId && savedLineups.some(x => x.id === lineup.id)
              ? <Favorite style={{ cursor: canFavorite ? 'pointer' : 'not-allowed', color: '#f44336', fontSize: 20 }} />
              : <FavoriteBorderOutlined style={{ cursor: canFavorite ? 'pointer' : 'not-allowed', color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />
            }
          </IconButton>
        </Stack>
      </Stack>

      {/* Row 2: Player names (full width) */}
      <Stack sx={{ fontSize: '12px', color: lineup.players.some(p => p.name !== '') ? 'rgba(255, 255, 255, 0.87)' : 'rgba(255, 255, 255, 0.4)', lineHeight: 1.4 }}>
        {lineup.players.map(p => p.name).filter(x => x !== '').join(', ') || 'No Players Selected...'}
      </Stack>

      {/* Row 3: Stats + actions */}
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Stack direction='row' gap={isMobile ? 1.5 : 2} fontSize={isMobile ? '11px' : '13px'} color={lineup.simResults?.simRoi ? 'rgba(255,255,255,0.87)' : 'rgba(255,255,255,0.4)'}>
          <Stack>FPP: {lineup.players.reduce((acc, player) => acc + player.fpp, 0).toFixed(1)}</Stack>
          <Stack>ROI: {lineup.simResults?.simRoi || '-'}</Stack>
          <Stack>Win: {lineup.simResults?.winPct || '-'}</Stack>
        </Stack>
        <Stack direction='row'>
          <IconButton aria-label="Duplicate lineup" onClick={(e) => { e.stopPropagation(); onDupeLineup(); }} sx={{ color: 'rgba(255,255,255,0.5)', width: 40, height: 40 }}>
            <CopyAll sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton aria-label="Delete lineup" onClick={(e) => { e.stopPropagation(); onDeleteLineup(); }} sx={{ color: '#F14659', width: 40, height: 40 }}>
            <Delete sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Lineups